/**
 * Full-screen status shown while the call is connecting, or when it cannot connect.
 * Without it the page stays blank white until RealtimeKit is ready, and forever if
 * init fails, so guests could not tell "still loading" from "broken".
 *
 * @param {{ status: 'loading' | 'slow' | 'error' | 'invalid-link', onRetry?: () => void }} props
 */
export const JOIN_STATUS_MESSAGES = {
  loading: {
    title: 'Joining the video call…',
    detail: 'This can take a few seconds.',
  },
  slow: {
    title: 'Still connecting…',
    detail: 'This is taking longer than usual. Check your internet connection, or try again.',
  },
  error: {
    title: "We couldn't connect you to the video call.",
    detail:
      'Please try again. If it keeps happening, close this tab and open the link from your invitation again.',
  },
  'invalid-link': {
    title: 'This video call link is incomplete.',
    detail: 'Please open the link from your invitation again.',
  },
};

export function JoinStatus({ status, onRetry }) {
  const message = JOIN_STATUS_MESSAGES[status] ?? JOIN_STATUS_MESSAGES.loading;
  const showSpinner = status === 'loading' || status === 'slow';
  const showRetry = (status === 'slow' || status === 'error') && typeof onRetry === 'function';

  return (
    <div className="join-status" role={status === 'error' ? 'alert' : 'status'} aria-live="polite">
      {showSpinner && <div className="join-status__spinner" aria-hidden="true" />}
      <h1 className="join-status__title">{message.title}</h1>
      <p className="join-status__detail">{message.detail}</p>
      {showRetry && (
        <button type="button" className="join-status__retry" onClick={onRetry}>
          Try again
        </button>
      )}
      <style>{`
        .join-status {
          box-sizing: border-box;
          min-height: 100vh;
          padding: 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background-color: #f9ebeb;
          color: #3a2a2a;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
        }
        .join-status__title {
          margin: 16px 0 8px;
          font-size: 22px;
          font-weight: 600;
        }
        .join-status__detail {
          margin: 0;
          max-width: 420px;
          font-size: 16px;
          line-height: 1.5;
        }
        .join-status__retry {
          margin-top: 20px;
          padding: 12px 28px;
          border: 0;
          border-radius: 8px;
          background-color: #b03a4a;
          color: #ffffff;
          font-size: 16px;
          cursor: pointer;
        }
        .join-status__retry:focus-visible {
          outline: 3px solid #3a2a2a;
          outline-offset: 2px;
        }
        .join-status__spinner {
          width: 44px;
          height: 44px;
          border: 4px solid rgba(176, 58, 74, 0.25);
          border-top-color: #b03a4a;
          border-radius: 50%;
          animation: join-status-spin 0.9s linear infinite;
        }
        @keyframes join-status-spin {
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .join-status__spinner { animation-duration: 3s; }
        }
      `}</style>
    </div>
  );
}
